// src/pages/admin/events/AdminEvents.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Switch,
  Space,
  Tag,
  Select,
  DatePicker,
  Typography,
  message,
  Popconfirm,
  Upload,
  Progress,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import RichText from "../RichTextEditor/RichTextEditor";
import TableWithExport from "../../../generic components/TableWithExport/TableWithExport";
import { uploadOne } from "../../../utils/utils";

const { Text } = Typography;

// Helpers
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const rawFile = (f) => (f?.originFileObj ? f.originFileObj : f);

const API = {
  listAdmin: routes.eventsAdminList,
  create: routes.createEvent,
  item: (id) =>
    routes.eventsAdminItem ? routes.eventsAdminItem(id) : `/api/events/${id}`,
  update: (id) =>
    routes.updateEvent ? routes.updateEvent(id) : `/api/events/${id}`,
  delete: (id) =>
    routes.deleteEvent ? routes.deleteEvent(id) : `/api/events/${id}`,
  duplicate: (id) =>
    routes.duplicateEvent
      ? routes.duplicateEvent(id)
      : `/api/events/${id}/duplicate`,
  publish: (id) =>
    routes.publishEvent ? routes.publishEvent(id) : `/api/events/${id}/publish`,
  projectsSearch: routes.capacityPublicList || "/api/capacity/public/projects",
};

export default function AdminEvents() {
  // table + filters
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState("");
  const [projectFilter, setProjectFilter] = useState(null);
  const [publishedFilter, setPublishedFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // modal form
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // project select options
  const [projLoading, setProjLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);

  // Banner upload state
  const [bannerFiles, setBannerFiles] = useState([]); // max 1
  const [bannerPct, setBannerPct] = useState(0);

  // Gallery upload state
  const [galleryFiles, setGalleryFiles] = useState([]); // multiple
  const [galleryPctMap, setGalleryPctMap] = useState({}); // { uid: % }
  const [galleryOverall, setGalleryOverall] = useState(0);

  // Save / preview UI
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, src: "", title: "" });

  // ────────────────────────────────────────────────────────────────
  // Fetch events
  // ────────────────────────────────────────────────────────────────
  const fetchList = async () => {
    try {
      setLoading(true);
      const params = { page, limit: pageSize };
      if (q.trim()) params.q = q.trim();
      if (projectFilter) params.project = projectFilter;
      if (publishedFilter !== "all") params.published = publishedFilter;
      if (dateRange?.length === 2) {
        params.from = dateRange[0]?.toISOString?.();
        params.to = dateRange[1]?.toISOString?.();
      }
      const { data } = await axios.get(API.listAdmin, { params });
      const list = data?.events || data?.rows || [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(data?.total ?? list.length);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load events");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line
  }, [page, pageSize, publishedFilter, projectFilter]);

  // Projects search
  const loadProjectOptions = async (keyword = "") => {
    try {
      setProjLoading(true);
      const { data } = await axios.get(API.projectsSearch, {
        params: { q: keyword, page: 1, limit: 20 },
      });
      const items = data?.projects || data?.rows || [];
      setProjectOptions(
        items.map((p) => ({ value: p._id, label: p.name, slug: p.slug }))
      );
    } catch {
      /* ignore */
    } finally {
      setProjLoading(false);
    }
  };
  useEffect(() => {
    loadProjectOptions("");
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Create / Edit / Delete / Duplicate / Publish
  // ────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setBannerFiles([]);
    setGalleryFiles([]);
    setBannerPct(0);
    setGalleryPctMap({});
    setGalleryOverall(0);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title || "",
      summary: record.summary || "",
      contentHtml: record.contentHtml || "",
      contentText: record.contentText || "",
      project: record.project?._id || record.project || null,
      location: record.location || "",
      startDate: record.startDate ? dayjs(record.startDate) : null,
      endDate: record.endDate ? dayjs(record.endDate) : null,
      bannerImage: record.bannerImage || "",
      tags: Array.isArray(record.tags) ? record.tags : [],
      isPublished:
        typeof record.isPublished === "boolean" ? record.isPublished : false,
      isAvailable:
        typeof record.isAvailable === "boolean" ? record.isAvailable : false,
    });

    // Banner fileList (existing doesn't re-upload)
    setBannerFiles(
      record.bannerImage
        ? [
            {
              uid: "banner-1",
              name: record.bannerImage.split("/").pop() || "banner",
              status: "done",
              url: record.bannerImage,
            },
          ]
        : []
    );

    // Gallery fileList (existing doesn't re-upload)
    setGalleryFiles(
      Array.isArray(record.gallery)
        ? record.gallery.map((url, i) => ({
            uid: `g-${i}`,
            name: url.split("/").pop() || `image-${i + 1}`,
            status: "done",
            url,
          }))
        : []
    );

    setBannerPct(0);
    setGalleryPctMap({});
    setGalleryOverall(0);
    setModalOpen(true);
  };

  // Upload all NEW gallery files (skip existing URLs)
  const uploadGalleryNew = async (files = []) => {
    if (!files.length) return [];

    const progressMap = {};
    const recalcOverall = () => {
      const vals = Object.values(progressMap);
      const avg =
        vals.length > 0
          ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
          : 0;
      setGalleryOverall(avg);
    };

    const urls = await Promise.all(
      files.map(async (f) => {
        // Keep existing URL (unchanged)
        if (!f.originFileObj && f.url) return f.url;

        // Upload new file
        const uid = f.uid || Math.random().toString(36);
        const url = await uploadOne(rawFile(f), (pct) => {
          progressMap[uid] = pct;
          setGalleryPctMap((m) => ({ ...m, [uid]: pct }));
          recalcOverall();
        });
        return url;
      })
    );

    setGalleryOverall(100);
    return urls.filter(Boolean);
  };

  const submitForm = async (values) => {
    setSaving(true);
    setBannerPct(0);
    setGalleryPctMap({});
    setGalleryOverall(0);

    // Banner URL – upload only if a NEW file is selected
    let bannerUrl = (values.bannerImage || "").trim();
    if (bannerFiles[0]?.originFileObj) {
      bannerUrl = await uploadOne(rawFile(bannerFiles[0]), setBannerPct);
      setBannerPct(100);
    } else if (!bannerUrl && bannerFiles[0]?.url) {
      bannerUrl = bannerFiles[0].url;
    }

    // Gallery URLs – keep existing, upload only new files
    const galleryUrls = await uploadGalleryNew(galleryFiles);

    const payload = {
      ...values,
      bannerImage: bannerUrl,
      gallery: galleryUrls, // <— flat array of strings
      startDate: values.startDate ? values.startDate.toDate() : null,
      endDate: values.endDate ? values.endDate.toDate() : null,
    };

    // Ensure contentText exists
    if (!payload.contentText && payload.contentHtml) {
      const tmp = payload.contentHtml
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      payload.contentText = tmp.slice(0, 5000);
    }

    try {
      if (editing) {
        await axios.patch(API.update(editing._id), payload);
        message.success("Event updated");
      } else {
        await axios.post(API.create, payload);
        message.success("Event created");
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteOne = async (record) => {
    try {
      await axios.delete(API.delete(record._id));
      message.success("Event deleted");
      const isLastOnPage = rows.length === 1 && page > 1;
      if (isLastOnPage) setPage((p) => p - 1);
      else fetchList();
    } catch {
      message.error("Delete failed");
    }
  };

  const duplicateOne = async (record) => {
    try {
      await axios.patch(API.duplicate(record._id));
    } catch {
      message.error("Duplicate failed");
    } finally {
      fetchList();
    }
  };

  const setPublish = async (record, checked) => {
    try {
      await axios.patch(API.publish(record._id), { publish: checked });
      setRows((prev) =>
        prev.map((r) =>
          r._id === record._id
            ? {
                ...r,
                isPublished: checked,
                publishDate: checked ? new Date() : null,
              }
            : r
        )
      );
    } catch {
      message.error("Failed to update publish state");
    }
  };

  // Columns
  const columns = useMemo(
    () => [
      {
        title: "#",
        width: 64,
        align: "center",
        render: (_v, _r, i) => (page - 1) * pageSize + i + 1,
      },
      {
        title: "Title",
        dataIndex: "title",
        sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
        render: (v) => (
          <Text strong ellipsis={{ tooltip: v }}>
            {v || "—"}
          </Text>
        ),
      },
      {
        title: "Project",
        dataIndex: ["project", "name"],
        render: (_v, r) => r?.project?.name || "—",
        sorter: (a, b) =>
          (a?.project?.name || "").localeCompare(b?.project?.name || ""),
      },
      {
        title: "Dates",
        render: (_, r) => (
          <span>
            {r.startDate ? fmtDate(r.startDate) : "—"}
            {r.endDate ? ` → ${fmtDate(r.endDate)}` : ""}
          </span>
        ),
        sorter: (a, b) =>
          new Date(a.startDate || 0) - new Date(b.startDate || 0),
      },
      { title: "Location", dataIndex: "location", render: (v) => v || "—" },
      {
        title: "Published",
        width: 150,
        render: (_, r) => (
          <Space>
            <Switch
              checked={!!r.isPublished}
              onChange={(c) => setPublish(r, c)}
            />
            {r.isPublished ? (
              <Tag color="green">Published</Tag>
            ) : (
              <Tag>Pending</Tag>
            )}
          </Space>
        ),
      },
      {
        title: "Created",
        width: 200,
        sorter: (a, b) =>
          new Date(a.created_at || 0) - new Date(b.created_at || 0),
        render: (_, r) => fmtDate(r.created_at),
      },
      {
        title: "Actions",
        fixed: "right",
        width: 200,
        render: (_, record) => (
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
            <Button
              icon={<CopyOutlined />}
              onClick={() => duplicateOne(record)}
            />
            <Popconfirm
              title="Delete event?"
              okText="Delete"
              okType="danger"
              onConfirm={() => deleteOne(record)}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <div>
      <h2 className="adminTitle" style={{ marginBottom: 16 }}>
        Events
      </h2>

      <TableWithExport
        columns={columns}
        data={rows}
        loading={loading}
        tableName="events"
        filters={false}
        enableReorder={true}
        onReorder={async (ids) => {
          await axios.patch(routes.reorderEvent, { ids });
          await fetchList();
        }}
        addRow={openAdd}
        getRowKey={(r) => r._id}
      />

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        title={editing ? `Edit: ${editing.title}` : "Add Event"}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        okText={editing ? "Save" : "Create"}
        confirmLoading={saving}
        destroyOnClose
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitForm}
          initialValues={{
            title: "",
            summary: "",
            contentHtml: "",
            contentText: "",
            project: null,
            location: "",
            startDate: null,
            endDate: null,
            bannerImage: "",
            tags: [],
            isPublished: false,
            isAvailable: false,
          }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="Event title" />
          </Form.Item>

          <Form.Item label="Project" name="project">
            <Select
              showSearch
              placeholder="Search & select a project"
              filterOption={false}
              onSearch={loadProjectOptions}
              options={projectOptions}
              loading={projLoading}
            />
          </Form.Item>

          <Form.Item label="Summary" name="summary">
            <Input.TextArea rows={3} placeholder="Short teaser/summary" />
          </Form.Item>

          <Form.Item
            label="Content"
            name="contentHtml"
            rules={[
              { required: true, message: "Content is required" },
              {
                validator: (_, v) => {
                  const plain = String(v || "")
                    .replace(/<[^>]+>/g, " ")
                    .trim();
                  return plain.length >= 10
                    ? Promise.resolve()
                    : Promise.reject("Minimum 10 characters");
                },
              },
            ]}
          >
            <RichText
              value={form.getFieldValue("contentHtml")}
              onChange={(html, text) =>
                form.setFieldsValue({ contentHtml: html, contentText: text })
              }
            />
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item label="Start Date" name="startDate">
              <DatePicker />
            </Form.Item>
            <Form.Item label="End Date" name="endDate">
              <DatePicker />
            </Form.Item>
            <Form.Item label="Location" name="location">
              <Input placeholder="e.g. Cairo, Egypt or Online" />
            </Form.Item>
          </Space>

          {/* Banner (deferred upload) */}
          <Form.Item label="Banner Image">
            <Space align="start" wrap style={{ gap: 12 }}>
              <Upload
                listType="picture-card"
                fileList={bannerFiles}
                beforeUpload={() => false}
                onChange={({ fileList }) => setBannerFiles(fileList.slice(-1))}
                onPreview={(file) =>
                  setPreview({
                    open: true,
                    src: file.url || file.thumbUrl,
                    title: file.name || "Preview",
                  })
                }
                maxCount={1}
                accept="image/*"
              >
                {bannerFiles.length >= 1 ? null : (
                  <div style={{ width: 140 }}>
                    <UploadOutlined />{" "}
                    <div style={{ marginTop: 4 }}>Upload</div>
                  </div>
                )}
              </Upload>

              {saving && bannerFiles[0]?.originFileObj && (
                <div style={{ width: 180, alignSelf: "center" }}>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Uploading…</div>
                  <Progress percent={bannerPct} size="small" />
                </div>
              )}
            </Space>
          </Form.Item>

          {/* Gallery (deferred upload) */}
          <Form.Item label="Gallery">
            <Upload
              listType="picture-card"
              multiple
              fileList={galleryFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => setGalleryFiles(fileList)}
              onPreview={(file) =>
                setPreview({
                  open: true,
                  src: file.url || file.thumbUrl,
                  title: file.name || "Preview",
                })
              }
              accept="image/*"
            >
              <div style={{ width: 140 }}>
                <UploadOutlined /> <div style={{ marginTop: 4 }}>Upload</div>
              </div>
            </Upload>

            {/* Progress while saving new gallery files */}
            {saving && galleryFiles.some((f) => f.originFileObj) && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                  Gallery progress
                </div>
                <Progress percent={galleryOverall} />
                {/* optional per-file */}
                {galleryFiles
                  .filter((f) => f.originFileObj)
                  .map((f) => (
                    <div key={f.uid} style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{f.name}</div>
                      <Progress
                        percent={galleryPctMap[f.uid] || 0}
                        size="small"
                      />
                    </div>
                  ))}
              </div>
            )}
          </Form.Item>

          <Form.Item label="Tags" name="tags">
            <Select
              mode="tags"
              placeholder="Type and press Enter to add tags"
            />
          </Form.Item>

          <Space>
            <Form.Item
              label="Published"
              name="isPublished"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Available for booking"
              name="isAvailable"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Shared image preview */}
      <Modal
        open={preview.open}
        title={preview.title}
        footer={null}
        onCancel={() => setPreview((p) => ({ ...p, open: false }))}
      >
        <img alt="preview" style={{ width: "100%" }} src={preview.src} />
      </Modal>
    </div>
  );
}
